import { useCallback, useEffect, useRef } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { toast } from 'react-toastify'

import { DropDown as GameDropdown } from 'components/atomic/GameDropdown'
import { UploadHint } from 'components/UploadHint'

import { useApp } from 'store/app/hooks'
import { useTeam } from 'store/team/hooks'
import { useAuth } from 'store/auth/hooks'
import { useGame } from 'store/game/hooks'

import DefaultAvatar from 'assets/avatar.webp'

import { track } from 'utils/analytics'
import { CreateTeamInput } from './types'
import './CreateTeamModal.css'

const slugifyTeamName = (name?: string) =>
  (name || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40)

export const CreateTeamModal = () => {
  const { isCreateTeamModalOpen, setIsCreateTeamModalOpen } = useApp()
  const { isCreateTeam, newTeam, error, createTeam } = useTeam()
  const { games, getAllGames } = useGame()
  const { updateUserState } = useAuth()

  const {
    handleSubmit,
    formState: { errors },
    control,
    reset,
    watch,
    setValue,
  } = useForm<CreateTeamInput>()

  const coverPhoto = watch('cover_photo')
  const avatar = watch('avatar')
  const teamName = watch('team_name')

  // Track whether the user has manually edited the URL; once they do,
  // we stop auto-syncing from the team name so we don't clobber their edit.
  const urlTouchedRef = useRef(false)

  // Live slug: as the user types the team name, fill the URL with its slug —
  // unless they've manually touched the URL field.
  useEffect(() => {
    if (urlTouchedRef.current) return
    setValue('team_url', slugifyTeamName(teamName), { shouldDirty: false })
  }, [teamName, setValue])

  // Fetch games when modal opens if not already loaded
  useEffect(() => {
    if (isCreateTeamModalOpen && (!games || games.length === 0)) {
      getAllGames()
    }
  }, [isCreateTeamModalOpen, games, getAllGames])

  useEffect(() => {
    urlTouchedRef.current = false
    reset({
      team_name: '',
      team_url: '',
      game_id: '',
      team_description: '',
      avatar: undefined,
      cover_photo: undefined,
    })
  }, [isCreateTeamModalOpen, reset])

  const onSubmit = useCallback(
    async (data: CreateTeamInput) => {
      try {
        const createdTeam = await createTeam(data)
        const teamId = createdTeam.teamId || createdTeam.team?._id

        if (teamId) {
          updateUserState({ teamId })
          toast.success(createdTeam.msg || 'Team created successfully!')
          track('team_created', { team_name: data.team_name })
        }

        urlTouchedRef.current = false
        reset({
          team_name: '',
          team_url: '',
          game_id: '',
          team_description: '',
          avatar: undefined,
          cover_photo: undefined,
        })

        setIsCreateTeamModalOpen(false)
      } catch (error: any) {
        toast.error(error?.errors || error?.message || 'Failed to create team')
      }
    },
    [createTeam, reset, updateUserState, setIsCreateTeamModalOpen],
  )

  useEffect(() => {
    if (error) {
      toast.error(error)
    }
  }, [error])

  useEffect(() => {
    if (!isCreateTeam && newTeam) {
      setIsCreateTeamModalOpen(false)
    }
  }, [isCreateTeam, newTeam, setIsCreateTeamModalOpen])

  if (!isCreateTeamModalOpen) return null

  return (
    <div className="create-team-modal">
      <div className="ctm-overlay" onClick={() => setIsCreateTeamModalOpen(false)}>
        <div className="ctm-modal" onClick={(e) => e.stopPropagation()}>
          {/* Header */}
          <div className="ctm-header">
            <button
              type="button"
              className="ctm-close-btn"
              onClick={() => setIsCreateTeamModalOpen(false)}
              aria-label="Close"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
            <h2 className="ctm-title">Create Your Team</h2>
            <p className="ctm-subtitle">Build your competitive roster and dominate the arena</p>
          </div>

          {/* Form */}
          <form className="ctm-form" onSubmit={handleSubmit(onSubmit)}>
            <div className="ctm-content">
              {/* Images Row - Cover Photo + Team Logo side by side */}
              <div className="ctm-images-row">
                {/* Cover Photo Upload */}
                <Controller
                  control={control}
                  name="cover_photo"
                  render={({ field }) => (
                    <div className="ctm-field">
                      <label className="ctm-label">Cover Photo</label>
                      <label
                        htmlFor="cover-upload"
                        className={`ctm-cover-upload ${field.value && field.value.size > 0 ? 'has-image' : ''} ${errors?.cover_photo ? 'has-error' : ''}`}
                      >
                        {field.value && field.value.size > 0 ? (
                          <img
                            src={URL.createObjectURL(field.value)}
                            className="ctm-cover-image"
                            alt="Cover preview"
                          />
                        ) : null}
                        <div className="ctm-cover-placeholder">
                          <div className="ctm-upload-icon">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z" />
                              <circle cx="12" cy="13" r="4" />
                            </svg>
                          </div>
                          <span className="ctm-upload-text">
                            {field.value ? 'Change Cover' : 'Upload Cover'}
                          </span>
                        </div>
                        <input
                          accept="image/*"
                          className="hidden"
                          id="cover-upload"
                          type="file"
                          onChange={(e) => {
                            const file = e.target.files?.[0]
                            if (file) field.onChange(file)
                          }}
                        />
                      </label>
                    </div>
                  )}
                />

                {/* Team Logo */}
                <div className="ctm-avatar-section">
                  <span className="ctm-avatar-label">Team Logo</span>
                  <Controller
                    control={control}
                    name="avatar"
                    render={({ field }) => (
                      <label htmlFor="avatar-upload" className="ctm-avatar-upload">
                        <div className={`ctm-avatar-ring ${errors?.avatar ? 'has-error' : ''}`}>
                          <div className="ctm-avatar-inner">
                            {field.value && field.value.size > 0 ? (
                              <img
                                src={URL.createObjectURL(field.value)}
                                className="ctm-avatar-image"
                                alt="Team logo"
                              />
                            ) : (
                              <div className="ctm-avatar-placeholder">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                  <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                                </svg>
                              </div>
                            )}
                          </div>
                        </div>
                        <input
                          accept="image/*"
                          className="hidden"
                          id="avatar-upload"
                          type="file"
                          onChange={(e) => {
                            const file = e.target.files?.[0]
                            if (file) field.onChange(file)
                          }}
                        />
                      </label>
                    )}
                  />
                  <span className="ctm-avatar-hint">
                    {avatar ? 'Click to change' : 'Click to upload'}
                  </span>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 -mt-2">
                <UploadHint type="banner" />
                <UploadHint type="avatar" />
              </div>

              {/* Form Fields */}
              <div className="ctm-form-fields">
                {/* Team Name */}
                <Controller
                  control={control}
                  name="team_name"
                  rules={{ required: 'Team name is required' }}
                  render={({ field }) => (
                    <div className="ctm-field">
                      <label className="ctm-label">Team Name</label>
                      <input
                        type="text"
                        className={`ctm-input ${errors?.team_name ? 'has-error' : ''}`}
                        placeholder="Enter your team name"
                        value={field.value || ''}
                        onChange={field.onChange}
                      />
                      {errors?.team_name && (
                        <span className="ctm-error">{errors.team_name.message}</span>
                      )}
                    </div>
                  )}
                />

                {/* Team URL */}
                <Controller
                  control={control}
                  name="team_url"
                  render={({ field }) => (
                    <div className="ctm-field">
                      <label className="ctm-label">
                        Team URL <span className="ctm-label-optional">(auto-filled from name)</span>
                      </label>
                      <div className="ctm-url-wrapper">
                        <span className="ctm-url-prefix">cyberlete.net/team/</span>
                        <input
                          type="text"
                          className={`ctm-input ${errors?.team_url ? 'has-error' : ''}`}
                          placeholder="your-team-name"
                          value={field.value || ''}
                          onChange={(e) => {
                            urlTouchedRef.current = true
                            field.onChange(e)
                          }}
                        />
                      </div>
                      {errors?.team_url && (
                        <span className="ctm-error">{errors.team_url.message}</span>
                      )}
                    </div>
                  )}
                />

                {/* Game Selection */}
                <Controller
                  control={control}
                  name="game_id"
                  rules={{ required: 'Please select a game' }}
                  render={({ field }) => (
                    <div className="ctm-field ctm-game-field">
                      <GameDropdown
                        value={field.value}
                        onChange={(val: string) => field.onChange(val)}
                        error={errors?.game_id?.message}
                      />
                    </div>
                  )}
                />

                {/* Team Description */}
                <Controller
                  control={control}
                  name="team_description"
                  render={({ field }) => (
                    <div className="ctm-field">
                      <label className="ctm-label">
                        Description <span className="ctm-label-optional">(optional)</span>
                      </label>
                      <textarea
                        className={`ctm-input ctm-textarea ${errors?.team_description ? 'has-error' : ''}`}
                        placeholder="Tell others about your team..."
                        value={field.value || ''}
                        onChange={field.onChange}
                        rows={3}
                      />
                      {errors?.team_description && (
                        <span className="ctm-error">{errors.team_description.message}</span>
                      )}
                    </div>
                  )}
                />
              </div>

              {/* Info Banner */}
              <div className="ctm-info-banner">
                <svg className="ctm-info-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <path d="M12 16v-4M12 8h.01" />
                </svg>
                <div className="ctm-info-content">
                  <div className="ctm-info-title">Captain Approval Required</div>
                  <div className="ctm-info-text">
                    Players will request to join your team. You can approve or decline requests in Team Management — no password needed.
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="ctm-footer">
              <button
                type="submit"
                className="ctm-submit-btn"
                disabled={isCreateTeam}
              >
                {isCreateTeam ? (
                  <>
                    <span className="ctm-spinner" />
                    Creating...
                  </>
                ) : (
                  <>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <path d="M12 5v14M5 12h14" />
                    </svg>
                    Create Team
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}
